import React, {useMemo, useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, AnalyticsMetadataForContext, AnalyticsModalType} from 'constants/AnalyticsConstants';
import {MinimalExpirationInfo} from 'constants/SampleStatusConstants';
import {ContainerPropertiesUpdate} from 'samples/ContainerDAO';
import {bulkUpdateContainerPropertiesWithDataMods} from 'samples/StorableActions';
import BaseBulkEditSamplesModal from 'samples/toolbar/BaseBulkEditSamplesModal';
import CurrentAndNewValueRow from 'samples/toolbar/CurrentAndNewValueRow';
import {
  EXPIRATION_DATETIME_FORMATS,
  EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT,
  validateAndConvertExpirationDatetime,
} from 'samples/valid-contents/ValidContentsConstants';
import DatetimeField from 'ui-library/inputs/DatetimeField';
import {readableTime} from 'util/DateUtil';

export interface BulkUpdateExpirationDateOverrideModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (selectedIds: Array<string>) => void;
  analyticsMetadata: AnalyticsMetadataForContext;
  items: Array<{id: string; expirationInfo?: MinimalExpirationInfo}>;
}

const MULTIPLE_VALUES_SENTINEL = 'MULTIPLE_VALUES_SENTINEL';
function getCurrentExpirationDateOverride(
  items: Array<{id: string; expirationInfo?: MinimalExpirationInfo}>
): string | null | typeof MULTIPLE_VALUES_SENTINEL {
  const dedupedCurrentValues = new Set(
    items.map(
      (item) => (item.expirationInfo?.isOverridden ? item.expirationInfo?.expirationDate : null) ?? null
    )
  );
  if (dedupedCurrentValues.size === 1) {
    const [date] = dedupedCurrentValues;
    return date;
  } else {
    return MULTIPLE_VALUES_SENTINEL;
  }
}

function hasInheritedExpirationDates(
  items: Array<{id: string; expirationInfo?: MinimalExpirationInfo}>
): boolean {
  return items.some(
    (item) => item.expirationInfo?.expirationDate != null && !item.expirationInfo?.isOverridden
  );
}

function getReadableDate(currentDate: string | null | typeof MULTIPLE_VALUES_SENTINEL): string {
  if (currentDate === MULTIPLE_VALUES_SENTINEL) {
    return 'Multiple values';
  } else if (currentDate == null) {
    return 'Not specified';
  }
  return readableTime(currentDate);
}

export default function BulkUpdateExpirationDateOverrideModal(
  props: BulkUpdateExpirationDateOverrideModalProps
): JSX.Element {
  const {currentDate, originalExpirationDate, itemIds, hasInheritedDates} = useMemo(() => {
    const currentDate = getCurrentExpirationDateOverride(props.items);
    return {
      currentDate,
      originalExpirationDate: currentDate === MULTIPLE_VALUES_SENTINEL ? null : currentDate,
      itemIds: props.items.map(({id}) => id),
      hasInheritedDates: hasInheritedExpirationDates(props.items),
    };
  }, [props.items]);

  const [expirationDateOverride, setExpirationDateOverride] = useState<string | null>(originalExpirationDate);
  const [shouldClearExpirationDate, setShouldClearExpirationDate] = useState(false);

  const analyticsMetadataDict = {
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_EXPIRATION_DATE_OVERRIDE,
  };

  const handleRequestSubmit = (selectedIds: Array<string>, inputExpirationDate: string | null): void => {
    const {doUpdate, expirationDateOverride} = validateAndConvertExpirationDatetime(inputExpirationDate);
    if (doUpdate) {
      const containerUpdates: Array<ContainerPropertiesUpdate> = selectedIds.map((id) => ({
        id,
        expirationDateOverride,
      }));
      bulkUpdateContainerPropertiesWithDataMods(containerUpdates, analyticsMetadataDict);
      props.onRequestClose();
    }
  };

  const onChangeClearExpirationDate = (clearExpirationDate: boolean): void => {
    setShouldClearExpirationDate(clearExpirationDate);
    setExpirationDateOverride(clearExpirationDate ? null : originalExpirationDate);
  };

  const modalContent = (
    <CurrentAndNewValueRow
      label="value"
      currentValue={getReadableDate(currentDate)}
      newValueInput={
        <DatetimeField
          autoFocus={false}
          labelStyle={DatetimeField.LABEL_STYLES.HIDDEN}
          placeholder="Date and time"
          onChange={setExpirationDateOverride}
          value={expirationDateOverride}
          isDisabled={shouldClearExpirationDate}
          preventDefault={true}
          datetimePresentationalFormat={EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT}
          validDatetimeInputFormats={EXPIRATION_DATETIME_FORMATS}
        />
      }
    />
  );

  return (
    <BaseBulkEditSamplesModal
      modalTitle="Bulk update expiration date override"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={(selectedIds) => {
        handleRequestSubmit(selectedIds, expirationDateOverride);
      }}
      itemIds={itemIds}
      analyticsMetadata={analyticsMetadataDict}
      modalContent={modalContent}
      clearFieldCheckboxProps={{
        shouldShowClearCheckbox: () => currentDate != null,
        shouldClear: shouldClearExpirationDate,
        onChangeShouldClear: onChangeClearExpirationDate,
      }}
      isSubmitDisabled={() => expirationDateOverride == null && !shouldClearExpirationDate}
      dataTestAttrs={{component: 'BulkUpdateExpirationDateOverrideModal', element: 'root'}}
      sampleStatusParams={{
        trackingParams: {
          actionAttempted: 'update_expiration_date',
          actionOrigin: props.analyticsMetadata.actionOrigin ?? ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
          sampleIds: itemIds,
          eventGroupId: props.analyticsMetadata.eventGroupId ?? Analytics.makeAnalyticsEventId(),
        },
      }}
      infoNoticeProps={
        hasInheritedDates && !shouldClearExpirationDate
          ? {
              text: 'Some containers inherit expiration date from their contents.',
              supportingInfo: "Updating those containers' expiration dates will override the inheritance.",
            }
          : shouldClearExpirationDate
            ? {
                text: 'Containers will return to inheriting expiration date from their contents.',
              }
            : undefined
      }
    />
  );
}
